import React, { useEffect, useState } from 'react';
import { Users, Copy, CheckCircle, Info, ExternalLink } from 'lucide-react';
import { getUsernameFromProfileId } from '../../../../services/firestoreService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * ReferralsEditor - Editor component for the referrals block
 * Shows referral link preview and customization options
 */
const ReferralsEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [username, setUsername] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUsername = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        const fetchedUsername = await getUsernameFromProfileId(profileId);
        setUsername(fetchedUsername);
      } catch (error) {
        console.error('Error loading username:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsername();
  }, [profileId]);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const referralLink = username ? `https://chessweb.site/signup?ref=${username}` : '';

  const copyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <div className="animate-spin w-5 h-5 border-2 border-gray-600 border-t-[#749555] rounded-full mr-2"></div>
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info Card */}
      <div className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#749555]/20 rounded-lg">
            <Users size={24} className="text-[#749555]" />
          </div>
          <div className="flex-1">
            <h4 className="text-white font-medium mb-1">Referral Program</h4>
            <p className="text-sm text-gray-400">
              Be rewarded when you help others create their chess website
            </p>
          </div>
        </div>
      </div>

      {/* Referral Link Preview */}
      {username && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Your Referral Link
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={referralLink}
              readOnly
              className="flex-1 px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-gray-400 text-sm"
            />
            <button
              onClick={copyLink}
              className="px-3 py-2 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors"
              title="Copy link"
            >
              {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
            </button>
          </div>
        </div>
      )}

      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Join ChessWeb"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Description
        </label>
        <textarea
          value={content.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Create your own chess website for free!"
          rows={3}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none"
        />
      </div>

      {/* Button Text */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Button Text
        </label>
        <input
          type="text"
          value={content.buttonText || ''}
          onChange={(e) => handleChange('buttonText', e.target.value)}
          placeholder="Sign Up"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Show Share Buttons Toggle */}
      <div className="flex items-center justify-between bg-[#1a1a2e] border border-gray-700 rounded-lg p-3">
        <div>
          <p className="text-sm font-medium text-white">Show Share Buttons</p>
          <p className="text-xs text-gray-500">Twitter, Facebook, Email share options</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={content.showShareButtons !== false}
            onChange={(e) => handleChange('showShareButtons', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#749555]"></div>
        </label>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
        <SizeSpacingPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={BLOCK_FEATURES['referrals']}
        />
        <ColorsFontsPanel
          style={content.style || {}}
          onChange={handleStyleChange}
          features={BLOCK_FEATURES['referrals']}
          theme={theme || {}}
          blockType="referrals"
        />
      </>)}

      {/* Info */}
      <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-200">
            When visitors sign up using your link, they'll be tracked as your referrals.
          </p>
        </div>
      </div>

      {/* Manage Referrals Link */}
      <a
        href="/dashboard/website/referrals"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors text-sm font-medium"
      >
        Manage Referrals
        <ExternalLink size={14} />
      </a>
    </div>
  );
};

export default ReferralsEditor;
